import { useCallback, useEffect, useMemo } from 'react';
import { useComponentSize, useMutableRef } from '@carnica/utils/hooks';
import { ITEMS_GAP, ProgressBarVariables, generateMask, getLineSize } from "./utils";

/**
 * @description хук который отвечает за отрисовку линии прогресса и его анимацию
 */
export var useProgressLine = function useProgressLine(progressBarRef, options) {
  var interval = options.interval,
    steps = options.steps,
    iterative = options.iterative,
    currentStep = options.currentStep;
  var size = getLineSize(useComponentSize(progressBarRef).width, steps);
  var sizeRef = useMutableRef(size);
  var currentStepRef = useMutableRef(currentStep);
  var setProperty = function setProperty(type, value) {
    var _progressBarRef$curre;
    var propertyValue;
    if (type === ProgressBarVariables.TRANSITION) {
      propertyValue = "linear width ".concat(value !== null && value !== void 0 ? value : interval, "ms");
    } else if (type === ProgressBarVariables.MASK) {
      propertyValue = value === null || value === void 0 ? void 0 : value.toString();
    } else if (type === ProgressBarVariables.WIDTH || type === ProgressBarVariables.OFFSET) {
      propertyValue = "".concat(value, "px");
    }
    (_progressBarRef$curre = progressBarRef.current) === null || _progressBarRef$curre === void 0 ? void 0 : _progressBarRef$curre.style.setProperty(type, propertyValue || null);
  };
  var removeProperty = function removeProperty(type) {
    var _progressBarRef$curre2;
    return (_progressBarRef$curre2 = progressBarRef.current) === null || _progressBarRef$curre2 === void 0 ? void 0 : _progressBarRef$curre2.style.removeProperty(type);
  };
  var setProgressWidth = function setProgressWidth(width, withoutTransition) {
    if (withoutTransition) {
      removeProperty(ProgressBarVariables.TRANSITION);
    }
    setProperty(ProgressBarVariables.WIDTH, width);
  };
  var setProgressWidthByIndex = useCallback(function (index, withOffset, withoutTransition, ignoreStepComparison) {
    // Целое количество пройденных шагов
    var calculatedSize = Math.floor(index) * sizeRef.current + Math.floor(Math.min(index, steps - 1)) * ITEMS_GAP;
    var stepWidth = index % 1 * sizeRef.current;
    var getWidth = function getWidth() {
      // Если не пошаговый решим то ширина = ширина пройденных шагов + ширина текущего шага
      if (!iterative) {
        return calculatedSize + stepWidth;
      }

      // Если анимация шага не завершена и уже началась
      if (index % 1 !== 0) {
        return stepWidth;
      }

      // Если шаг еще не перключился (конец анимации)
      if (!ignoreStepComparison && index !== currentStepRef.current) {
        return sizeRef.current;
      }
      return 0;
    };
    setProgressWidth(getWidth(), withoutTransition);
    if (withOffset) {
      // Если используется итеративный переход, то ставим отступ равный пройденным шагам
      setProperty(ProgressBarVariables.OFFSET, iterative ? calculatedSize : 0);
    }
  }, [steps, iterative]);
  var setProgressOffset = function setProgressOffset(offset) {
    return setProperty(ProgressBarVariables.OFFSET, offset);
  };
  var setTransition = useMemo(function () {
    return {
      on: function on(time) {
        return setProperty(ProgressBarVariables.TRANSITION, time);
      },
      off: function off() {
        return removeProperty(ProgressBarVariables.TRANSITION);
      }
    };
  }, []);
  useEffect(function () {
    var lines = new Array(steps).fill(size);
    setProperty(ProgressBarVariables.MASK, generateMask(size, lines));
  }, [size, steps]);
  return useMemo(function () {
    return {
      setProgressWidth: setProgressWidth,
      setTransition: setTransition,
      setProgressOffset: setProgressOffset,
      setProgressWidthByIndex: setProgressWidthByIndex,
      size: size
    };
  }, [size, setProgressWidthByIndex]);
};